import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { ChatIcon, CopyIcon, EffectsIcon, MicIcon, VideoIcon, YanoLogo } from "./Icons"
import { PhoneXMarkIcon } from "@heroicons/react/24/solid"
import { UsersIcon, LockClosedIcon, LockOpenIcon, SpeakerXMarkIcon, SpeakerWaveIcon, ChatBubbleLeftRightIcon, XMarkIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline"
import { XCircleIcon, CheckCircleIcon } from "@heroicons/react/24/solid"
import { useI18n } from "../i18n"
import { api } from "../api"
import { subscribeToUserChannel } from "../cable"
import { VIDEO_GRADES, filterStringForVideoEffects, normalizeVideoEffects } from "../videoEffects"
import { useFaceBlurOverlay } from "../hooks/useFaceBlurOverlay"
import { waitingStateLabelKey } from "./callUiState"
import { DM_INITIAL_STATE, dmReducer } from "./dmState"

function initialsFor(label) {
  const trimmed = label.trim()
  if (/^\p{Emoji}/u.test(trimmed)) return trimmed
  return trimmed
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => Array.from(part)[0]?.toUpperCase() || "")
    .join("") || "Y"
}

function avatarGenderLabel(t, value) {
  if (value === "woman") return t("profile.gender.woman")
  if (value === "man") return t("profile.gender.man")
  if (value === "unspecified") return t("profile.gender.unspecified")
  return ""
}

function ProfileGenderIcon({ value }) {
  if (value === "woman") {
    return (
      <svg viewBox="0 0 20 20" className="h-4.5 w-4.5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="10" cy="7" r="3.2" />
        <path d="M10 10.4v6.1M7.2 13.7H12.8" />
      </svg>
    )
  }

  if (value === "man") {
    return (
      <svg viewBox="0 0 20 20" className="h-4.5 w-4.5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="7.2" cy="12.8" r="3.2" />
        <path d="M9.8 10.2L15.2 4.8M11.4 4.8H15.2V8.6" />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 20 20" className="h-4.5 w-4.5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="10" cy="10" r="6" />
      <path d="M7.6 12.4c.7.7 1.5 1 2.4 1s1.7-.3 2.4-1M8 8.1h.01M12 8.1h.01" />
    </svg>
  )
}

function ProfileGenderPill({ value, label }) {
  return (
    <span className={`yano-sheet-profile__pill yano-sheet-profile__pill--${value}`}>
      <span className="yano-sheet-profile__pill-icon" aria-hidden="true">
        <ProfileGenderIcon value={value} />
      </span>
      <span>{label}</span>
    </span>
  )
}

function AvatarBadge({ name, avatarUrl, className = "", imgClassName = "", children = null }) {
  const initials = initialsFor(name || "?")

  return (
    <span className={`relative ${className}`.trim()} aria-hidden="true">
      {avatarUrl ? (
        <span className="absolute inset-0 overflow-hidden rounded-[inherit]">
          <img src={avatarUrl} alt="" className={imgClassName} />
        </span>
      ) : (
        initials
      )}
      {children}
    </span>
  )
}

function videoTrackFor(participant) {
  return participant?.tracks?.video?.persistentTrack || participant?.tracks?.video?.track || participant?.videoTrack || null
}

function audioTrackFor(participant) {
  return participant?.tracks?.audio?.persistentTrack || participant?.tracks?.audio?.track || participant?.audioTrack || null
}

function displayNameFor(participant, fallbackLabel = "") {
  return participant?.user_name || participant?.userData?.display_name || fallbackLabel
}

function formatDuration(seconds) {
  const total = Math.max(0, Math.floor(seconds))
  const minutes = String(Math.floor(total / 60)).padStart(2, "0")
  const remainder = String(total % 60).padStart(2, "0")
  return `${minutes}:${remainder}`
}

function stateForTrack(participant, kind) {
  return participant?.tracks?.[kind]?.state || "missing"
}

function hasPlayableTrack(participant, kind) {
  return stateForTrack(participant, kind) === "playable"
}

function ParticipantVideoFx({ track, mirrored = false, muted = false, className = "", effects, fit = "cover" }) {
  const baseRef = useRef(null)
  const blurCanvasRef = useRef(null)
  const lastTrackRef = useRef(track)
  const normalized = useMemo(() => normalizeVideoEffects(effects || {}), [
    effects?.grade, effects?.soft, effects?.vignette, effects?.faceBlur, effects?.faceBlurPx
  ])
  const filter = useMemo(() => filterStringForVideoEffects(normalized), [normalized])

  // Keep track ref current so face blur survives brief track nulls during renegotiation.
  if (track) lastTrackRef.current = track
  const effectiveTrack = track || lastTrackRef.current

  async function safePlay(element) {
    if (!element) return
    element.muted = true
    element.volume = 0
    element.playsInline = true

    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        await element.play()
        return
      } catch (_error) {
        await new Promise((resolve) => window.setTimeout(resolve, 180))
      }
    }
  }

  useFaceBlurOverlay({
    enabled: Boolean(normalized.faceBlur && effectiveTrack),
    sourceVideoRef: baseRef,
    overlayRef: blurCanvasRef,
    holdMs: 1200,
    lerpA: 0.22,
    fit
  })

  useEffect(() => {
    const base = baseRef.current
    if (!base) return undefined

    if (!track) {
      base.srcObject = null
      return undefined
    }

    const stream = new MediaStream([ track ])
    base.srcObject = stream
    safePlay(base)

    const handleMetadata = () => safePlay(base)
    base.addEventListener("loadedmetadata", handleMetadata)
    base.addEventListener("canplay", handleMetadata)

    return () => {
      if (baseRef.current) baseRef.current.srcObject = null
      base.removeEventListener("loadedmetadata", handleMetadata)
      base.removeEventListener("canplay", handleMetadata)
    }
  }, [track])

  const videoClassName = `${className} ${mirrored ? "scale-x-[-1]" : ""}`.trim()

  return (
    <div
      className="relative h-full w-full overflow-hidden"
      style={{
        "--yano-face-blur": `${normalized.faceBlurPx}px`
      }}
    >
      <video
        ref={baseRef}
        autoPlay
        playsInline
        muted
        className={`pointer-events-none ${videoClassName}`}
        style={{ filter }}
      />

      <canvas
        ref={blurCanvasRef}
        className={`yano-camera-faceblur yano-camera-canvas pointer-events-none absolute inset-0 h-full w-full ${mirrored ? "scale-x-[-1]" : ""}`}
        style={{ display: normalized.faceBlur ? "block" : "none" }}
      />

      <div
        aria-hidden
        className={`pointer-events-none absolute inset-0 yano-preview-vignette ${normalized.vignette ? "yano-preview-vignette--on" : ""}`}
      />
    </div>
  )
}

function ParticipantAudio({ track }) {
  const mediaRef = useRef(null)

  useEffect(() => {
    if (!mediaRef.current) return undefined

    if (!track) {
      mediaRef.current.srcObject = null
      return undefined
    }

    const stream = new MediaStream([ track ])
    mediaRef.current.srcObject = stream
    mediaRef.current.play().catch(() => {})

    return () => {
      if (mediaRef.current) {
        mediaRef.current.srcObject = null
      }
    }
  }, [track])

  return <audio ref={mediaRef} autoPlay playsInline />
}

function TileStatus({ muted, videoOff }) {
  const { t } = useI18n()

  return (
    <div className="flex items-center gap-2 text-[11px] font-semibold">
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 ${muted ? "bg-violet-500/10 text-violet-300/60" : "bg-[rgb(126_235_122_/_0.15)] text-[rgb(126_235_122_/_0.9)]"}`}>
        <MicIcon />
        {muted ? t("call.muted_label") : t("call.mic_on")}
      </span>
      {videoOff ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/10 px-2 py-1 text-violet-300/60">
          <VideoIcon />
          {t("call.camera_off")}
        </span>
      ) : null}
    </div>
  )
}

function DmBadge({ unread = 0, compact = false }) {
  if (unread > 0) {
    return (
      <span className={`yano-dm-badge yano-dm-badge--unread`}>
        <ChatBubbleLeftRightIcon />
        <span>{unread > 9 ? "9+" : unread}</span>
      </span>
    )
  }
  return (
    <span className="yano-dm-badge">
      <ChatBubbleLeftRightIcon />
      {!compact ? <span>DM</span> : null}
    </span>
  )
}

function ParticipantTile({ participant, isLocal = false, avatarUrl = "", fallbackLabel = "", effectsOverride = null, hideOverlay = false, latestMessage = null, onProfileTap = null, dmInfo = null }) {
  const { t } = useI18n()
  const videoTrack = videoTrackFor(participant)
  const videoOn = hasPlayableTrack(participant, "video") && Boolean(videoTrack)
  const micOn = hasPlayableTrack(participant, "audio")
  const displayName = displayNameFor(participant, fallbackLabel)
  const initials = initialsFor(displayName)
  const effects = effectsOverride || participant?.userData?.yano_fx

  const handleProfileTap = (event) => {
    event.stopPropagation()
    onProfileTap?.(participant, isLocal)
  }

  return (
    <article className="yano-call-tile">
      {videoOn ? (
        <>
          <ParticipantVideoFx track={videoTrack} mirrored={isLocal} muted={isLocal} effects={effects} fit="contain" className="h-full w-full object-contain" />
          {latestMessage ? (
            <div className="absolute inset-x-3 top-3 z-[5]">
              <TileMessage message={latestMessage} />
            </div>
          ) : null}
        </>
      ) : (
        <div className="flex h-full items-center justify-center bg-[radial-gradient(circle_at_50%_30%,rgba(139,92,246,0.15),transparent_60%),#130a22]">
          <div className="text-center">
            <button
              type="button"
              onClick={handleProfileTap}
              className="relative mx-auto transition active:scale-95"
              aria-label={dmInfo?.active ? t("call.dm_active_indicator") : t("call.profile_open_actions")}
            >
              <AvatarBadge
                name={displayName}
                avatarUrl={avatarUrl}
                className="grid h-24 w-24 place-items-center rounded-full border-2 border-violet-400/15 bg-violet-500/12 text-3xl font-semibold text-violet-200 transition hover:bg-violet-500/18 hover:border-violet-400/25"
                imgClassName="h-full w-full object-cover"
              >
              {dmInfo?.active ? (
                <span className={`yano-dm-dot ${dmInfo.unread > 0 ? "" : "yano-dm-dot--read"}`} style={{ top: "-2px", right: "-2px" }}>
                  {dmInfo.unread > 0 ? (dmInfo.unread > 9 ? "9+" : dmInfo.unread) : null}
                </span>
              ) : null}
              </AvatarBadge>
            </button>
            {latestMessage ? (
              <div className="mx-auto mt-5 max-w-[80%]">
                <TileMessage message={latestMessage} variant="audio" />
              </div>
            ) : (
              <p className="mt-4 text-sm text-violet-400/60">{micOn ? t("call.listening") : t("call.audio_muted")}</p>
            )}
          </div>
        </div>
      )}

      <div className={`yano-ui-layer absolute inset-x-3 bottom-3 flex items-end justify-between gap-3 ${hideOverlay ? "yano-ui-layer--hidden" : ""}`}>
        <button
          type="button"
          onClick={handleProfileTap}
          className={`relative flex items-center gap-2 rounded-2xl px-3 py-2 text-left text-sm text-violet-100 backdrop-blur-md transition active:scale-[0.97] ${dmInfo?.active ? "bg-violet-600/35 hover:bg-violet-600/45" : "bg-[#1a0e2e]/80 hover:bg-[#1a0e2e]/95"}`}
          aria-label={dmInfo?.active ? t("call.dm_active_indicator") : t("call.profile_open_actions")}
        >
          {dmInfo?.active ? (
            <ChatBubbleLeftRightIcon className="h-3.5 w-3.5 shrink-0 text-violet-200" />
          ) : null}
          <span className="font-semibold">{displayName}</span>
          {isLocal ? <span className="text-violet-400/60">{t("call.you_label")}</span> : null}
          {dmInfo?.active && dmInfo.unread > 0 ? (
            <span className="ml-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-pink-500 px-1.5 text-[9px] font-extrabold leading-none text-white shadow-[0_3px_8px_-2px_rgba(236,72,153,0.6)]">
              {dmInfo.unread > 9 ? "9+" : dmInfo.unread}
            </span>
          ) : null}
        </button>
        <TileStatus muted={!micOn} videoOff={!videoOn} />
      </div>
    </article>
  )
}

function TileMessage({ message, variant = "overlay" }) {
  const [age, setAge] = useState(() => message._receivedAt ? Date.now() - message._receivedAt : 0)

  useEffect(() => {
    if (!message._receivedAt) return
    const interval = window.setInterval(() => setAge(Date.now() - message._receivedAt), 500)
    return () => window.clearInterval(interval)
  }, [message._receivedAt, message.id])

  const fresh = age < 5000
  const fading = age > 20000
  const opacity = fading ? Math.max(0, 1 - (age - 20000) / 10000) : 1

  const glowOpacity = fresh ? Math.max(0, 1 - age / 5000) : 0

  if (variant === "audio") {
    return (
      <div className="relative mx-auto mt-1.5 max-w-[12rem]" style={{ opacity }}>
        {fresh ? <div className="absolute -inset-3 rounded-2xl transition-opacity duration-1000" style={{ opacity: glowOpacity, background: "radial-gradient(ellipse at center, rgba(139, 92, 246, 0.35), transparent 70%)" }} /> : null}
        <p className="relative rounded-xl bg-violet-500/12 px-3.5 py-2 text-sm font-semibold text-violet-100">
          {message.body}
        </p>
      </div>
    )
  }

  return (
    <div className="relative inline-block max-w-[85%]" style={{ opacity }}>
      {fresh ? <div className="absolute -inset-4 rounded-3xl transition-opacity duration-1000" style={{ opacity: glowOpacity, background: "radial-gradient(ellipse at center, rgba(139, 92, 246, 0.4), transparent 70%)" }} /> : null}
      <div className="relative rounded-2xl rounded-tl-sm bg-black/50 px-3.5 py-2.5 text-sm font-semibold text-white/90 backdrop-blur-sm">
        {message.body}
      </div>
    </div>
  )
}

async function destroyDailyCallObject(callObject) {
  if (!callObject) return

  try {
    await callObject.leave().catch(() => {})
  } finally {
    callObject.destroy()
  }
}

function AudioIndicator({ name, avatarUrl = "", isLocal = false, speaking = false, latestMessage = null, onTap = null, dmInfo = null }) {
  const { t } = useI18n()

  const Wrapper = onTap ? "button" : "div"
  const wrapperProps = onTap
    ? {
        type: "button",
        onClick: onTap,
        "aria-label": dmInfo?.active ? t("call.dm_active_indicator") : t("call.profile_open_actions"),
      }
    : {}

  return (
    <Wrapper
      {...wrapperProps}
      className={`flex flex-col items-center gap-3 text-center ${onTap ? "transition active:scale-[0.97]" : ""}`}
    >
      <AvatarBadge
        name={name}
        avatarUrl={avatarUrl}
        className={`grid h-20 w-20 place-items-center rounded-full bg-violet-500/12 border-2 text-2xl font-semibold text-violet-200 transition-colors ${speaking ? "border-violet-400/40" : "border-violet-400/15"}`}
        imgClassName="h-full w-full object-cover"
      >
        {speaking ? (
          <span className="absolute inset-0 rounded-full border-2 border-violet-400/30 animate-[waiting-pulse_1.8s_cubic-bezier(0.16,1,0.3,1)_infinite]" />
        ) : null}
        {!speaking ? (
          <span className="absolute -bottom-0.5 -right-0.5 grid h-5 w-5 place-items-center rounded-full bg-red-500/90 shadow-[0_2px_6px_rgba(239,68,68,0.35)]">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round">
              <rect x="9" y="2" width="6" height="12" rx="3" />
              <path d="M5 10a7 7 0 0 0 14 0" />
              <line x1="4" y1="4" x2="20" y2="20" />
            </svg>
          </span>
        ) : null}
        {dmInfo?.active ? (
          <span className={`yano-dm-dot ${dmInfo.unread > 0 ? "" : "yano-dm-dot--read"}`} style={{ top: "-4px", right: "-4px" }}>
            {dmInfo.unread > 0 ? (dmInfo.unread > 9 ? "9+" : dmInfo.unread) : null}
          </span>
        ) : null}
      </AvatarBadge>
      <div>
        <div className="flex items-center justify-center gap-1.5">
          <p className="text-sm font-bold text-violet-200">{name}</p>
          {isLocal ? (
            <span className="rounded-full border border-violet-400/14 bg-violet-500/8 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-violet-300/55">
              {t("call.you")}
            </span>
          ) : null}
        </div>
        {latestMessage ? (
          <TileMessage message={latestMessage} variant="audio" />
        ) : (
          <p className="mt-0.5 text-xs text-violet-400/50">{speaking ? t("call.speaking") : t("call.listening")}</p>
        )}
      </div>
    </Wrapper>
  )
}

function WaitingTile({ roomCode, shareUrl, onCopyLink, copied, status = "connecting" }) {
  const { t } = useI18n()

  return (
    <article className="yano-call-tile yano-call-tile--waiting">
      <div className="text-center">
        <div className="mx-auto mb-5 flex items-center justify-center">
          <div className="yano-waiting-signal">
            <span className="yano-waiting-signal__ring" />
            <span className="yano-waiting-signal__dot" />
          </div>
        </div>
        <p className="font-display text-2xl leading-none text-violet-100">{t(waitingStateLabelKey(status))}</p>
        {roomCode || shareUrl ? (
          <div className="mt-4 flex flex-col items-center gap-3">
            {roomCode ? (
              <>
                <div className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-violet-300/55">
                  {t("call.invite_code")}
                </div>
                <div className="inline-flex items-center justify-center rounded-full bg-violet-500/12 px-3 py-1.5 text-[11px] font-extrabold tracking-[0.2em] text-violet-200/90">
                  {roomCode}
                </div>
              </>
            ) : null}
            {shareUrl ? (
              <button
                type="button"
                onClick={onCopyLink}
                className="inline-flex items-center gap-2 rounded-2xl bg-violet-500/12 px-4 py-2.5 text-sm font-bold text-violet-300 transition hover:bg-violet-500/20"
              >
                <CopyIcon />
                {copied ? t("home.copied") : t("home.copy_link")}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </article>
  )
}

function ControlButton({ active = true, danger = false, label, onClick, children, wide = false, className: extraClassName = "", pressed = undefined }) {
  const className = [
    "yano-call-control",
    active ? "" : "yano-call-control--off",
    danger ? "yano-call-control--danger" : "",
    wide ? "yano-call-control--wide" : "",
    extraClassName
  ].filter(Boolean).join(" ")

  return (
    <button type="button" className={className} onClick={onClick} aria-label={label} title={label} aria-pressed={pressed}>
      {children}
      {wide ? <span>{label}</span> : null}
    </button>
  )
}

function ParticipantActionSheet({ target, onClose, onDm }) {
  const { t } = useI18n()
  const [mounted, setMounted] = useState(false)
  const [open, setOpen] = useState(false)
  const sheetRef = useRef(null)
  const dragStateRef = useRef({ startY: null, delta: 0 })

  // Mount, then trigger open animation on next frame
  useEffect(() => {
    if (target) {
      setMounted(true)
      const id = window.requestAnimationFrame(() => setOpen(true))
      return () => window.cancelAnimationFrame(id)
    }
    // Closing: play close animation, then unmount after transition
    setOpen(false)
    const timeout = window.setTimeout(() => setMounted(false), 480)
    return () => window.clearTimeout(timeout)
  }, [target])

  // Escape closes
  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault()
        onClose?.()
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onClose])

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return undefined
    const previous = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => { document.body.style.overflow = previous }
  }, [open])

  if (!mounted || !target) return null

  const initials = initialsFor(target.name || "?")
  const closeWithAction = () => onClose?.()
  const handleDm = () => {
    onDm?.(target)
    onClose?.()
  }
  const genderLabel = avatarGenderLabel(t, target.gender)

  // Drag-to-dismiss handlers
  const onPointerDown = (event) => {
    // Don't start drag on action buttons
    if (event.target.closest(".yano-sheet-action")) return
    dragStateRef.current.startY = event.clientY
    dragStateRef.current.delta = 0
    if (sheetRef.current) sheetRef.current.style.transition = "none"
  }
  const onPointerMove = (event) => {
    const { startY } = dragStateRef.current
    if (startY === null) return
    const delta = Math.max(0, event.clientY - startY)
    dragStateRef.current.delta = delta
    if (sheetRef.current) sheetRef.current.style.transform = `translateY(${delta}px)`
  }
  const onPointerEnd = () => {
    const { startY, delta } = dragStateRef.current
    if (startY === null) return
    if (sheetRef.current) {
      sheetRef.current.style.transition = ""
      sheetRef.current.style.transform = ""
    }
    dragStateRef.current.startY = null
    dragStateRef.current.delta = 0
    if (delta > 80) onClose?.()
  }

  return createPortal(
    <>
      <div
        className={`yano-sheet-scrim ${open ? "yano-sheet-scrim--open" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={sheetRef}
        className={`yano-sheet ${open ? "yano-sheet--open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={t("call.profile_actions")}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
      >
        <div className="yano-sheet-card">
          <div className="yano-sheet-grabber" />

          {/* Profile header */}
          <div className="yano-sheet-stagger flex flex-col">
            <div className="flex items-center gap-4 px-6 pb-4 pt-1">
              <AvatarBadge
                name={target.name}
                avatarUrl={target.avatarUrl}
                className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-violet-500/15 text-xl font-bold text-violet-200 ring-1 ring-inset ring-violet-400/20 shadow-[0_12px_28px_-12px_rgba(167,139,250,0.55)]"
                imgClassName="h-full w-full object-cover"
              />
              <div className="min-w-0 flex-1">
                <p className="font-display text-2xl font-bold leading-tight tracking-tight text-violet-50 truncate">
                  {target.isLocal ? t("call.profile_role_you") : target.name}
                </p>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {target.isHostMember ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/14 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-violet-200">
                      {t("call.profile_role_host")}
                    </span>
                  ) : null}
                  {target.mutedByHost ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/10 px-2 py-0.5 text-[10.5px] font-semibold text-violet-300/70">
                      <SpeakerXMarkIcon className="h-3 w-3" />
                      {t("call.muted_by_host")}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            {genderLabel || target.bio ? (
              <div className="px-6 pb-4">
                <div className="yano-sheet-profile">
                  {genderLabel ? (
                    <div className="yano-sheet-profile__row">
                      <ProfileGenderPill value={target.gender} label={genderLabel} />
                    </div>
                  ) : null}
                  {target.bio ? (
                    <div className="yano-sheet-profile__row yano-sheet-profile__row--stacked">
                      <p className="yano-sheet-profile__bio">{target.bio}</p>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {/* Actions */}
            {!target.isLocal ? (
              <div className="flex flex-col px-3.5 pb-1">
                <button
                  type="button"
                  className="yano-sheet-action"
                  onClick={handleDm}
                >
                  <span className="yano-sheet-action__icon">
                    <ChatBubbleLeftRightIcon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-display text-[15px] font-bold leading-tight text-violet-50">
                      {t("call.profile_action_dm")}
                    </span>
                    <span className="mt-0.5 block text-[11.5px] font-medium text-violet-300/55">
                      {t("call.profile_action_dm_meta")}
                    </span>
                  </span>
                  <ChevronTrail />
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>,
    document.body
  )
}

function ChevronTrail() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="shrink-0 text-violet-300/35">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}

function OneToOneDmPanel({ target, messages = [], loading = false, sending = false, error = "", onMinimize, onSend, onAvatarTap, formatTime }) {
  const { t } = useI18n()
  const [mounted, setMounted] = useState(false)
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState("")
  const [, setTimeTick] = useState(0)
  const inputRef = useRef(null)
  const scrollRef = useRef(null)

  // Mount + slide-in animation
  useEffect(() => {
    if (target) {
      setMounted(true)
      setDraft("")
      const id = window.requestAnimationFrame(() => setOpen(true))
      return () => window.cancelAnimationFrame(id)
    }
    setOpen(false)
    const timeout = window.setTimeout(() => setMounted(false), 500)
    return () => window.clearTimeout(timeout)
  }, [target?.key])

  // Focus input when opened
  useEffect(() => {
    if (!open) return undefined
    const id = window.setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 380)
    return () => window.clearTimeout(id)
  }, [open])

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages.length])

  // Re-render periodically so relative DM timestamps like "just now"
  // roll over into clock times without waiting for another message/state change.
  useEffect(() => {
    if (!open || !messages.length) return undefined
    const id = window.setInterval(() => setTimeTick((value) => value + 1), 15_000)
    return () => window.clearInterval(id)
  }, [open, messages.length])

  // Escape minimizes (preserves the conversation), not closes
  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault()
        onMinimize?.()
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onMinimize])

  if (!mounted || !target) return null

  const initials = initialsFor(target.name || "?")
  const firstName = (target.name || "").split(/\s+/)[0] || target.name || ""

  const handleSend = async (event) => {
    event?.preventDefault()
    const text = draft.trim()
    if (!text) return
    const sent = await onSend?.(text)
    if (sent === false) return
    setDraft("")
  }

  return createPortal(
    <aside
      className={`yano-dm-panel ${open ? "yano-dm-panel--open" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label={t("call.dm_title")}
    >
      {/* Header */}
      <header className="yano-dm-header">
        <button
          type="button"
          className="yano-dm-avatar yano-dm-avatar--tappable"
          onClick={onAvatarTap}
          aria-label={t("call.profile_open_actions")}
          title={t("call.profile_actions")}
        >
          {target.avatarUrl ? <img src={target.avatarUrl} alt="" className="h-full w-full object-cover" /> : initials}
          <span className="yano-dm-avatar__online" />
        </button>

        <div className="min-w-0 flex-1">
          <p className="font-display text-[15px] font-bold leading-tight tracking-tight text-violet-50 truncate">
            {target.isLocal ? t("call.profile_role_you") : firstName}
          </p>
          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] font-semibold text-violet-300/60">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(74,222,128,0.55)]" />
            <span>{t("call.dm_status_online")}</span>
            <span className="opacity-40">·</span>
            <span className="truncate">{t("call.dm_in_room_with")}</span>
          </div>
        </div>

        <button
          type="button"
          className="yano-dm-iconbtn"
          onClick={onMinimize}
          aria-label={t("call.dm_minimize")}
          title={t("call.dm_minimize")}
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </header>

      {/* Conversation */}
      <div ref={scrollRef} className="yano-dm-body">
        <div className="yano-dm-system">
          <LockClosedIcon className="h-3 w-3" />
          <span>{t("call.dm_private_meta")}</span>
        </div>

        {loading ? (
          <div className="px-2 py-4 text-center text-sm font-medium text-violet-300/60">
            {t("chat.loading")}
          </div>
        ) : null}

        {!loading && !messages.length ? (
          <div className="px-2 py-4 text-center text-sm font-medium text-violet-300/60">
            {t("chat.empty")}
          </div>
        ) : null}

        {messages.map((m, idx) => {
          const prev = messages[idx - 1]
          const showTime = !prev || prev.from !== m.from
          return (
            <React.Fragment key={m.id}>
              <div className={`yano-dm-bubble yano-dm-bubble--${m.from}`}>
                {m.text}
              </div>
              {showTime ? (
                <div className={`yano-dm-bubble-time yano-dm-bubble-time--${m.from}`}>
                  {formatTime?.(m.createdAt) || t("call.dm_just_now")}
                </div>
              ) : null}
            </React.Fragment>
          )
        })}

        {error ? (
          <div className="px-2 py-4 text-center text-sm font-medium text-violet-300/70">
            {error}
          </div>
        ) : null}
      </div>

      {/* Input */}
      <form className="yano-dm-input-wrap" onSubmit={handleSend}>
        <div className="yano-dm-input">
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t("call.dm_input_placeholder")}
            aria-label={t("call.dm_input_placeholder")}
          />
          <button
            type="submit"
            className="yano-dm-send"
            disabled={sending || !draft.trim()}
            aria-label={t("call.dm_send")}
          >
            <PaperAirplaneIcon className="h-4 w-4" />
          </button>
        </div>
      </form>
    </aside>,
    document.body
  )
}

function DmPreviewToast({ toast, onTap, onDismiss }) {
  const { t } = useI18n()
  const [mounted, setMounted] = useState(false)
  const [open, setOpen] = useState(false)
  const TOAST_LIFE_MS = 4500

  useEffect(() => {
    if (toast) {
      setMounted(true)
      const id = window.requestAnimationFrame(() => setOpen(true))
      const lifetime = window.setTimeout(() => onDismiss?.(), TOAST_LIFE_MS)
      return () => {
        window.cancelAnimationFrame(id)
        window.clearTimeout(lifetime)
      }
    }
    setOpen(false)
    const t1 = window.setTimeout(() => setMounted(false), 480)
    return () => window.clearTimeout(t1)
  }, [toast?.key, toast?.id])

  if (!mounted || !toast) return null

  const initials = initialsFor(toast.target?.name || "?")
  const firstName = (toast.target?.name || "").split(/\s+/)[0] || toast.target?.name || ""

  return createPortal(
    <button
      type="button"
      className={`yano-dm-toast ${open ? "yano-dm-toast--open" : ""}`}
      onClick={onTap}
      aria-label={t("call.dm_toast_tap_to_open")}
    >
      <div className="yano-dm-toast__avatar" aria-hidden="true">{initials}</div>
      <div className="min-w-0 flex-1 text-left">
        <p className="yano-dm-toast__name truncate">{firstName}</p>
        <p className="yano-dm-toast__text">{toast.text}</p>
      </div>
    </button>,
    document.body
  )
}

export function CallStage({
  join,
  title,
  roomCode,
  shareUrl,
  copied,
  onCopyLink,
  onLeaveRoom,
  chatOpen = false,
  onToggleChat,
  unreadCount = 0,
  onSendMessage,
  chatOverlayMessages = [],
  apiToken = "",
  currentUserId = null,
  displayName = "",
  initialMedia = { audioEnabled: true, videoEnabled: true },
  localVideoEffects = null,
  isHost = false,
  roomLocked = false,
  roomMembers = [],
  onLockRoom,
  onUnlockRoom,
  onKickMember,
  onMuteMember,
  pendingMembers = [],
  onApproveMember,
  onDenyMember
}) {
  const { t } = useI18n()
  const callObjectRef = useRef(null)
  // Tracks any in-flight call-object destroy from a previous mount/cleanup so
  // that the next mount can await it before creating a new DailyIframe.
  // Without this, React StrictMode + HMR can race two `createCallObject`
  // calls and trigger "Duplicate DailyIframe instances are not allowed".
  const pendingDestroyRef = useRef(Promise.resolve())
  const initialMediaRef = useRef(initialMedia)
  const recordingStartedRef = useRef(false)
  const [status, setStatus] = useState("connecting")
  const [error, setError] = useState("")
  const [participants, setParticipants] = useState({})
  const [localAudioEnabled, setLocalAudioEnabled] = useState(false)
  const [localVideoEnabled, setLocalVideoEnabled] = useState(false)
  const [joinedAt, setJoinedAt] = useState(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [selfIsMain, setSelfIsMain] = useState(false)
  const [uiHidden, setUiHidden] = useState(false)
  const [layoutMode, setLayoutMode] = useState("auto")
  const [participantsOpen, setParticipantsOpen] = useState(false)
  const [actionSheetTarget, setActionSheetTarget] = useState(null)
  const [dmState, dispatchDm] = useReducer(dmReducer, DM_INITIAL_STATE)
  const dmStateRef = useRef(dmState)
  useEffect(() => { dmStateRef.current = dmState }, [dmState])
  const dmConnectionRef = useRef(null)
  const [chatDraft, setChatDraft] = useState("")
  const [chatSending, setChatSending] = useState(false)
  const chatInputRef = useRef(null)
  const [keyboardOpen, setKeyboardOpen] = useState(false)
  const [leaveArmed, setLeaveArmed] = useState(false)
  const leaveTimerRef = useRef(null)
  const guestLabel = t("call.guest")
  const localDisplayName = displayName || guestLabel
  const lastSentUserDataRef = useRef("")
  const [effectsOpen, setEffectsOpen] = useState(false)
  const localFxDirtyRef = useRef(false)

  function loadFxFromStorage() {
    try {
      const grade = localStorage.getItem("yano_prejoin_grade")
      const soft = localStorage.getItem("yano_prejoin_soft")
      const vignette = localStorage.getItem("yano_prejoin_vignette")
      const faceBlur = localStorage.getItem("yano_prejoin_face_blur")
      const faceBlurAmount = localStorage.getItem("yano_prejoin_face_blur_amount")

      return {
        grade: grade || undefined,
        soft: soft ? soft === "1" : undefined,
        vignette: vignette ? vignette === "1" : undefined,
        faceBlur: faceBlur ? faceBlur === "1" : undefined,
        faceBlurPx: faceBlurAmount ? Number(faceBlurAmount) : undefined
      }
    } catch (_error) {
      return {}
    }
  }

  const [localFx, setLocalFx] = useState(() => normalizeVideoEffects({ ...(localVideoEffects || {}), ...loadFxFromStorage() }))

  useEffect(() => {
    // Keep local effects in sync with pre-join + storage unless the user tweaks effects in-call.
    if (localFxDirtyRef.current) return
    const next = normalizeVideoEffects({ ...(localVideoEffects || {}), ...loadFxFromStorage() })
    setLocalFx(next)
  }, [localVideoEffects, status])

  function persistLocalFx(nextFx) {
    try {
      localStorage.setItem("yano_prejoin_grade", nextFx.grade)
      localStorage.setItem("yano_prejoin_soft", nextFx.soft ? "1" : "0")
      localStorage.setItem("yano_prejoin_vignette", nextFx.vignette ? "1" : "0")
      localStorage.setItem("yano_prejoin_face_blur", nextFx.faceBlur ? "1" : "0")
      localStorage.setItem("yano_prejoin_face_blur_amount", String(nextFx.faceBlurPx))
    } catch (_error) {}
  }

  function updateLocalFx(patch) {
    localFxDirtyRef.current = true
    setLocalFx((current) => {
      const next = normalizeVideoEffects({ ...current, ...patch })
      persistLocalFx(next)
      return next
    })
  }
  const stubMediaRef = useRef({ stream: null, audioTrack: null, videoTrack: null })

  const updateStubParticipants = (audioTrack, videoTrack) => {
    const audioState = audioTrack && audioTrack.enabled ? "playable" : "off"
    const videoState = videoTrack && videoTrack.enabled ? "playable" : "off"

    setParticipants({
      local: {
        user_name: localDisplayName,
        user_id: currentUserId ? `user-${currentUserId}` : null,
        tracks: {
          audio: { state: audioState, track: audioTrack },
          video: { state: videoState, track: videoTrack }
        }
      }
    })
  }

  function handleVideoAreaClick(event) {
    if (event.target.closest("button, input, a, form, [data-pip], .yano-participants-panel, .yano-overlay-chat")) return
    if (chatOpen) {
      onToggleChat?.()
      setUiHidden(false)
      return
    }
    if (uiHidden) {
      setUiHidden(false)
    } else {
      setUiHidden(true)
    }
  }

  useEffect(() => {
    initialMediaRef.current = initialMedia
  }, [initialMedia])

  useEffect(() => {
    let active = true

    async function connect() {
      setStatus("connecting")
      setError("")
      setParticipants({})
      recordingStartedRef.current = false

      try {
        if (join?.vendor === "stub") {
          if (stubMediaRef.current.stream) {
            stubMediaRef.current.stream.getTracks().forEach((track) => track.stop())
          }

          stubMediaRef.current = { stream: null, audioTrack: null, videoTrack: null }
          updateStubParticipants(null, null)
          setStatus("live")
          setJoinedAt(Date.now())
          return
        }

        if (!join?.room_url || !join?.token) return

        // Wait for any previous-mount destroy (StrictMode / HMR) to finish
        // before we touch the Daily SDK. Otherwise we can race the destroy
        // and hit "Duplicate DailyIframe instances are not allowed".
        try { await pendingDestroyRef.current } catch (_) {}
        if (!active) return

        const module = await import("@daily-co/daily-js")
        if (!active) return

        const Daily = module.default || module

        const existingCallObject = callObjectRef.current || Daily.getCallInstance?.()
        if (existingCallObject) {
          await destroyDailyCallObject(existingCallObject)
          if (callObjectRef.current === existingCallObject) {
            callObjectRef.current = null
          }
        }
        if (!active) return

        const callObject = Daily.createCallObject({
          subscribeToTracksAutomatically: true
        })
        callObjectRef.current = callObject

        const syncParticipants = () => {
          if (!active) return

          const nextParticipants = Object.fromEntries(
            Object.entries(callObject.participants()).map(([ key, participant ]) => [
              key,
              participant ? {
                ...participant,
                tracks: participant.tracks ? { ...participant.tracks } : participant.tracks
              } : participant
            ])
          )

          setParticipants(nextParticipants)
        }

        callObject.on("joined-meeting", () => {
          setStatus("live")
          setJoinedAt(Date.now())
          syncParticipants()
        })
        callObject.on("left-meeting", () => {
          setStatus("ended")
          syncParticipants()
        })
        callObject.on("participant-joined", syncParticipants)
        callObject.on("participant-updated", syncParticipants)
        callObject.on("participant-left", syncParticipants)
        callObject.on("track-started", syncParticipants)
        callObject.on("track-stopped", syncParticipants)
        callObject.on("error", () => {
          setStatus("error")
          setError(t("call.connect_failed"))
        })
        callObject.on("camera-error", () => {
          setStatus("error")
          setError(t("call.camera_access_failed"))
        })

        await callObject.join({
          url: join.room_url,
          token: join.token,
          subscribeToTracksAutomatically: true,
          // Join first, then enable local devices after the meeting is live.
          // This avoids aborting the whole call on browsers that fail device init
          // during join() even though the room/token are valid.
          startVideoOff: true,
          startAudioOff: true
        })

        setStatus("live")
        setJoinedAt(Date.now())

        if (
          join?.recording?.auto_start_method === "call_object" &&
          join?.recording?.should_auto_start &&
          !recordingStartedRef.current
        ) {
          try {
            recordingStartedRef.current = true
            await callObject.startRecording({
              type: join.recording.type
            })
          } catch (recordingError) {
            const rawMessage = recordingError?.message || recordingError?.errorMsg || ""
            if (!/already.*record/i.test(rawMessage)) {
              setError(t("call.recording_failed"))
            }
          }
        }

        syncParticipants()
      } catch (event) {
        // Helpful for local debugging (permissions, invalid room urls, missing provider config, etc).
        // Keep the UI error localized + friendly.
        console.error("Failed to initialize call", {
          vendor: join?.vendor,
          room_url: join?.room_url,
          token_present: Boolean(join?.token),
          error: event
        })
        setStatus("error")
        setError(event?.name === "NotAllowedError" ? t("call.camera_access_failed") : t("call.initialize_failed"))
      }
    }

    connect()

    return () => {
      active = false
      if (callObjectRef.current) {
        const obj = callObjectRef.current
        callObjectRef.current = null
        // Chain onto pendingDestroyRef so the next mount can await it.
        pendingDestroyRef.current = pendingDestroyRef.current
          .then(() => destroyDailyCallObject(obj))
          .catch(() => {})
      }

      if (stubMediaRef.current.stream) {
        stubMediaRef.current.stream.getTracks().forEach((track) => track.stop())
        stubMediaRef.current = { stream: null, audioTrack: null, videoTrack: null }
      }
    }
  }, [join?.vendor, join?.room_url, join?.token])

  useEffect(() => {
    if (join?.vendor !== "stub") return
    if (status !== "live") return
    const { audioTrack, videoTrack } = stubMediaRef.current
    updateStubParticipants(audioTrack, videoTrack)
  }, [join?.vendor, currentUserId, localDisplayName, status])

  useEffect(() => {
    if (status !== "live") return
    const callObject = callObjectRef.current
    if (!callObject?.setUserData) return

    const fx = normalizeVideoEffects(localFx || {})
    const payload = JSON.stringify({ fx, display_name: localDisplayName })
    if (payload === lastSentUserDataRef.current) return
    lastSentUserDataRef.current = payload

    try {
      const existing = callObject.participants()?.local?.userData || {}
      callObject.setUserData({
        ...existing,
        display_name: localDisplayName,
        yano_fx: fx
      })
    } catch (_error) {
      // Best-effort; effects are decorative.
    }
  }, [status, localDisplayName, localFx])

  useEffect(() => {
    if (!joinedAt || status !== "live") {
      setElapsedSeconds(0)
      return undefined
    }

    const interval = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - joinedAt) / 1000))
    }, 1000)

    return () => {
      window.clearInterval(interval)
    }
  }, [joinedAt, status])

  const participantTiles = useMemo(() => {
    const entries = Object.entries(participants)
      .map(([ key, participant ]) => ({ key, participant }))
      .sort((left, right) => {
        if (left.key === "local") return 1
        if (right.key === "local") return -1
        return left.key.localeCompare(right.key)
      })

    return entries
  }, [participants])
  const participantTilesRef = useRef(participantTiles)
  const roomMembersRef = useRef(roomMembers)

  useEffect(() => {
    participantTilesRef.current = participantTiles
  }, [participantTiles])

  useEffect(() => {
    roomMembersRef.current = roomMembers
  }, [roomMembers])

  const remoteAudioTracks = useMemo(
    () => participantTiles
      .filter(({ key, participant }) => key !== "local" && hasPlayableTrack(participant, "audio"))
      .map(({ key, participant }) => ({
        key,
        track: audioTrackFor(participant)
      }))
      .filter(({ track }) => Boolean(track)),
    [participantTiles]
  )

  const localParticipant = participants.local || null
  const remoteParticipants = participantTiles.filter(({ key }) => key !== "local").map(({ participant }) => participant)
  const pendingJoinCount = isHost ? pendingMembers.length : 0
  const leadRemote = remoteParticipants[0] || null
  const resolvedLocalAudio = localParticipant ? hasPlayableTrack(localParticipant, "audio") : localAudioEnabled
  const resolvedLocalVideo = localParticipant ? hasPlayableTrack(localParticipant, "video") : localVideoEnabled
  const tileCount = Math.max(participantTiles.length, 2)
  const gridClassName = tileCount <= 2
    ? "grid-cols-1 md:grid-cols-2"
    : tileCount <= 4
      ? "grid-cols-1 sm:grid-cols-2"
      : "grid-cols-2 xl:grid-cols-3"
  const useFocusLayout = layoutMode === "focus"
    ? true
    : layoutMode === "grid"
      ? false
      : participantTiles.length <= 2
  const mainParticipant = useFocusLayout && (selfIsMain || !leadRemote) ? localParticipant : leadRemote
  const mainParticipantName = displayNameFor(mainParticipant, selfIsMain ? localDisplayName : guestLabel)
  const mainParticipantVideo = videoTrackFor(mainParticipant)
  const mainParticipantVideoOn = hasPlayableTrack(mainParticipant, "video") && Boolean(mainParticipantVideo)
  const pipParticipant = useFocusLayout
    ? (selfIsMain ? leadRemote : localParticipant)
    : null
  const pipName = pipParticipant
    ? displayNameFor(pipParticipant, pipParticipant === localParticipant ? localDisplayName : guestLabel)
    : ""
  const pipVideo = videoTrackFor(pipParticipant)
  const pipVideoOn = hasPlayableTrack(pipParticipant, "video") && Boolean(pipVideo)
  const remoteHasVideo = leadRemote ? hasPlayableTrack(leadRemote, "video") : false
  const remoteHasAudio = leadRemote ? hasPlayableTrack(leadRemote, "audio") : false
  const localHasVideo = resolvedLocalVideo
  const bothHaveVideo = remoteHasVideo && localHasVideo
  const neitherHasVideo = !remoteHasVideo && !localHasVideo
  const showPendingJoinNotice = pendingJoinCount > 0

  const formatDmTime = useCallback((value) => {
    if (!value) return t("call.dm_just_now")

    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) return t("call.dm_just_now")
    if (Date.now() - date.getTime() < 60_000) return t("call.dm_just_now")

    return new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit"
    }).format(date)
  }, [t])

  const normalizeDmMessage = useCallback((message) => ({
    id: message.id,
    from: message.sender?.id === currentUserId ? "you" : "them",
    text: message.body,
    createdAt: message.created_at,
  }), [currentUserId, formatDmTime])

  function latestMessageFor(participant) {
    if (!participant || !chatOverlayMessages.length) return null
    const pid = participant.user_id
    if (!pid) return null
    const backendId = parseInt(pid.replace("user-", ""), 10)
    if (!backendId) return null
    for (let i = chatOverlayMessages.length - 1; i >= 0; i--) {
      if (chatOverlayMessages[i].sender?.id === backendId) return chatOverlayMessages[i]
    }
    return null
  }

  const buildParticipantTarget = useCallback((participant, isLocal) => {
    if (!participant) return null
    const backendUserId = participant.user_id
      ? parseInt(String(participant.user_id).replace("user-", ""), 10)
      : null
    const member = backendUserId ? roomMembers.find((m) => m.id === backendUserId) : null
      return {
        key: participant.user_id || (isLocal ? "local" : participant.session_id || participant.user_name || "unknown"),
        participant,
        isLocal,
        backendUserId,
        name: displayNameFor(participant, isLocal ? localDisplayName : guestLabel),
        avatarUrl: member?.avatar_url || "",
        gender: member?.gender || "",
        bio: member?.bio || "",
        isHostMember: member?.role === "host",
        mutedByHost: Boolean(member?.muted_by_host),
      }
  }, [roomMembers, localDisplayName, guestLabel])

  const buildTargetForUser = useCallback((userId, fallbackName = guestLabel) => {
    if (!userId) return null

    const participantEntry = participantTilesRef.current.find(({ participant }) => {
      const backendUserId = participant?.user_id
        ? parseInt(String(participant.user_id).replace("user-", ""), 10)
        : null
      return backendUserId === userId
    })

    if (participantEntry) {
      const isLocal = participantEntry.key === "local"
      const member = roomMembersRef.current.find((item) => item.id === userId)
      return {
        key: participantEntry.participant.user_id || (isLocal ? "local" : participantEntry.participant.session_id || participantEntry.participant.user_name || `user-${userId}`),
        participant: participantEntry.participant,
        isLocal,
        backendUserId: userId,
        name: displayNameFor(participantEntry.participant, isLocal ? localDisplayName : fallbackName),
        avatarUrl: member?.avatar_url || "",
        gender: member?.gender || "",
        bio: member?.bio || "",
        isHostMember: member?.role === "host",
        mutedByHost: Boolean(member?.muted_by_host),
      }
    }

    const member = roomMembersRef.current.find((item) => item.id === userId)
    return {
      key: `user-${userId}`,
      participant: null,
      isLocal: false,
      backendUserId: userId,
      name: member?.display_name || fallbackName,
      avatarUrl: member?.avatar_url || "",
      gender: member?.gender || "",
      bio: member?.bio || "",
      isHostMember: member?.role === "host",
      mutedByHost: Boolean(member?.muted_by_host),
    }
  }, [guestLabel, localDisplayName])

  // Smart participant tap: if there's an active DM with this person, jump
  // straight to the conversation; otherwise show the action sheet.
  const handleParticipantTap = useCallback((participant, isLocal) => {
    const target = buildParticipantTarget(participant, isLocal)
    if (!target) return
    const existing = dmStateRef.current.conversations[target.key]
    if (existing) {
      dispatchDm({ type: "OPEN", target })
    } else {
      setActionSheetTarget(target)
    }
  }, [buildParticipantTarget])

  const closeActionSheet = useCallback(() => setActionSheetTarget(null), [])

  const avatarUrlForParticipant = useCallback((participant, isLocal = false) => {
    const backendUserId = participant?.user_id
      ? parseInt(String(participant.user_id).replace("user-", ""), 10)
      : (isLocal ? currentUserId : null)

    if (!backendUserId) return ""
    return roomMembersRef.current.find((member) => member.id === backendUserId)?.avatar_url || ""
  }, [currentUserId])

  // Sheet → DM transition
  const openDm = useCallback((target) => {
    if (!target) return
    dispatchDm({ type: "OPEN", target })
  }, [])

  const minimizeDm = useCallback(() => dispatchDm({ type: "MINIMIZE" }), [])

  const sendDmMessage = useCallback(async (text) => {
    const key = dmStateRef.current.currentKey
    const conv = key ? dmStateRef.current.conversations[key] : null
    const targetUserId = conv?.target?.backendUserId
    const body = text?.trim()
    if (!key || !targetUserId || !body || !apiToken || !roomCode) return false

    dispatchDm({ type: "SEND_START", key })

    try {
      const payload = await api.sendDirectMessage(apiToken, roomCode, targetUserId, body)
      if (!payload?.message) {
        dispatchDm({ type: "SEND_ERROR", key, error: t("chat.send_error") })
        return false
      }

      dispatchDm({
        type: "SEND",
        key,
        message: normalizeDmMessage(payload.message),
      })
      return true
    } catch (_error) {
      dispatchDm({ type: "SEND_ERROR", key, error: t("chat.send_error") })
      return false
    }
  }, [apiToken, normalizeDmMessage, roomCode, t])

  // Avatar tap inside an open DM → overlay the action sheet on top of the
  // DM (sheet z-index is above the DM panel). DM stays mounted underneath, so
  // dismissing the sheet returns the user to their conversation.
  const handleDmAvatarTap = useCallback(() => {
    const key = dmStateRef.current.currentKey
    const conv = key ? dmStateRef.current.conversations[key] : null
    if (conv?.target) setActionSheetTarget(conv.target)
  }, [])

  const dismissDmToast = useCallback(() => dispatchDm({ type: "DISMISS_TOAST" }), [])

  const handleToastTap = useCallback(() => {
    const t = dmStateRef.current.toast
    if (!t) return
    dispatchDm({ type: "OPEN", target: t.target })
  }, [])

  // Returns DM info for a given participant — used by tiles to render badges.
  const participantDmInfo = useCallback((participant, isLocal) => {
    if (!participant) return null
    const key = participant.user_id || (isLocal ? "local" : null)
    if (!key) return null
    const conv = dmState.conversations[key]
    if (!conv) return null
    return { active: true, unread: conv.unread || 0 }
  }, [dmState.conversations])

  // Derived: messages for the currently visible DM
  const currentDmConv = dmState.currentKey ? dmState.conversations[dmState.currentKey] : null
  const currentDmTarget = currentDmConv?.target || null
  const currentDmMessages = currentDmConv?.messages || []

  useEffect(() => {
    const key = currentDmTarget?.key
    const targetUserId = currentDmTarget?.backendUserId
    if (!key || !targetUserId || !apiToken || !roomCode) return
    if (currentDmConv?.loading || currentDmConv?.loaded) return

    let cancelled = false

    async function loadConversation() {
      dispatchDm({ type: "LOAD_START", key })

      try {
        const payload = await api.getDirectMessages(apiToken, roomCode, targetUserId)
        if (cancelled) return

        dispatchDm({
          type: "LOAD_SUCCESS",
          key,
          messages: (payload.messages || []).map(normalizeDmMessage),
        })
      } catch (_error) {
        if (!cancelled) {
          dispatchDm({ type: "LOAD_ERROR", key, error: "" })
        }
      }
    }

    loadConversation()

    return () => {
      cancelled = true
    }
  }, [apiToken, currentDmConv?.loaded, currentDmTarget?.backendUserId, currentDmTarget?.key, normalizeDmMessage, roomCode])

  useEffect(() => {
    if (!apiToken || !roomCode || !currentUserId) {
      if (dmConnectionRef.current) {
        dmConnectionRef.current.unsubscribe()
        dmConnectionRef.current = null
      }
      return undefined
    }

    const connection = subscribeToUserChannel(apiToken, {
      onEvent(data) {
        if (data?.type !== "direct_message") return
        if (data.room_code !== roomCode) return
        if (!data.other_user?.id || !data.message) return

        const target = buildTargetForUser(data.other_user.id, data.other_user.display_name || guestLabel)
        if (!target) return

        dispatchDm({
          type: "RECEIVE",
          key: target.key,
          target,
          message: normalizeDmMessage(data.message),
        })
      }
    })

    dmConnectionRef.current = connection

    return () => {
      connection.unsubscribe()
      if (dmConnectionRef.current === connection) {
        dmConnectionRef.current = null
      }
    }
  }, [apiToken, buildTargetForUser, currentUserId, guestLabel, normalizeDmMessage, roomCode])

  const videoParticipants = useMemo(
    () => participantTiles.filter(({ key, participant }) => {
      if (key === "local") return resolvedLocalVideo
      return hasPlayableTrack(participant, "video")
    }),
    [participantTiles, resolvedLocalVideo]
  )
  const nonVideoParticipants = useMemo(
    () => participantTiles.filter(({ key, participant }) => {
      if (key === "local") return !resolvedLocalVideo
      return !hasPlayableTrack(participant, "video")
    }),
    [participantTiles, resolvedLocalVideo]
  )

  async function handleChatSend() {
    const body = chatDraft.trim()
    if (!body || chatSending || !onSendMessage) return

    setChatSending(true)
    try {
      await onSendMessage(body)
      setChatDraft("")
    } catch (_error) {
      // silently fail — message stays in draft
    } finally {
      setChatSending(false)
    }
  }

  useEffect(() => {
    if (chatOpen && chatInputRef.current) {
      chatInputRef.current.focus()
    }
  }, [chatOpen])

  function handleLeaveClick() {
    if (leaveArmed) {
      clearTimeout(leaveTimerRef.current)
      setLeaveArmed(false)
      onLeaveRoom?.()
      return
    }

    setLeaveArmed(true)
    leaveTimerRef.current = setTimeout(() => setLeaveArmed(false), 3000)
  }

  useEffect(() => {
    return () => clearTimeout(leaveTimerRef.current)
  }, [])

  const isMobileRef = useRef(typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches)

  function handleChatInputFocus() {
    if (isMobileRef.current) setKeyboardOpen(true)
  }

  function handleChatInputBlur() {
    setKeyboardOpen(false)
  }

  async function toggleLocalAudio() {
    const nextValue = !resolvedLocalAudio
    setLocalAudioEnabled(nextValue)
    try {
      if (join?.vendor === "stub") {
        const current = stubMediaRef.current
        if (!nextValue && current.audioTrack) {
          current.audioTrack.enabled = false
          updateStubParticipants(current.audioTrack, current.videoTrack)
          return
        }

        if (nextValue) {
          if (current.audioTrack && current.audioTrack.readyState !== "ended") {
            current.audioTrack.enabled = true
            updateStubParticipants(current.audioTrack, current.videoTrack)
            return
          }

          const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
          const track = stream.getAudioTracks()[0] || null
          if (!track) {
            stream.getTracks().forEach((t) => t.stop())
            throw new Error("No audio track")
          }

          if (current.stream) {
            current.stream.addTrack(track)
            stream.getTracks().forEach((t) => t !== track && t.stop())
          } else {
            current.stream = stream
          }

          current.audioTrack = track
          updateStubParticipants(current.audioTrack, current.videoTrack)
          return
        }

        return
      }

      callObjectRef.current?.setLocalAudio(nextValue)
    } catch (_error) {
      setLocalAudioEnabled(!nextValue)
    }
  }

  async function toggleLocalVideo() {
    const nextValue = !resolvedLocalVideo
    setLocalVideoEnabled(nextValue)
    try {
      if (join?.vendor === "stub") {
        const current = stubMediaRef.current
        if (!nextValue && current.videoTrack) {
          current.videoTrack.enabled = false
          updateStubParticipants(current.audioTrack, current.videoTrack)
          return
        }

        if (nextValue) {
          if (current.videoTrack && current.videoTrack.readyState !== "ended") {
            current.videoTrack.enabled = true
            updateStubParticipants(current.audioTrack, current.videoTrack)
            return
          }

          const stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: true })
          const track = stream.getVideoTracks()[0] || null
          if (!track) {
            stream.getTracks().forEach((t) => t.stop())
            throw new Error("No video track")
          }

          if (current.stream) {
            current.stream.addTrack(track)
            stream.getTracks().forEach((t) => t !== track && t.stop())
          } else {
            current.stream = stream
          }

          current.videoTrack = track
          updateStubParticipants(current.audioTrack, current.videoTrack)
          return
        }

        return
      }

      callObjectRef.current?.setLocalVideo(nextValue)
    } catch (_error) {
      setLocalVideoEnabled(!nextValue)
    }
  }

  return (
    <div className="relative h-full overflow-hidden bg-[#1a0e2e]" onClick={handleVideoAreaClick}>
      <div className={`yano-ui-layer absolute inset-x-0 top-0 z-10 flex items-center justify-between gap-3 px-4 py-4 sm:px-5 ${uiHidden || keyboardOpen ? "yano-ui-layer--hidden" : ""}`}>
        <div className="flex min-w-0 items-center gap-3">
          <YanoLogo className="h-7 w-7" />
          <div className="h-5 w-px bg-violet-400/20" />
          <div className="flex min-w-0 items-center gap-2.5">
            <AvatarBadge
              name={mainParticipantName || title || t("call.room_fallback")}
              avatarUrl={avatarUrlForParticipant(mainParticipant, mainParticipant === localParticipant)}
              className="flex h-7 w-7 items-center justify-center rounded-full bg-violet-500/20 text-[10px] font-extrabold text-violet-200"
              imgClassName="h-full w-full object-cover"
            />
            <p className="truncate text-sm font-bold text-violet-200">{mainParticipantName || title || t("call.room_fallback")}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {participantTiles.length > 2 ? (
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-500/12 text-violet-300 transition hover:bg-violet-500/20"
              onClick={() => setLayoutMode((m) => m === "grid" ? "focus" : "grid")}
              aria-label={layoutMode === "grid" ? t("call.focus_view") : t("call.grid_view")}
              title={layoutMode === "grid" ? t("call.focus_view") : t("call.grid_view")}
            >
              {layoutMode === "grid" ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="15" x2="21" y2="15" /></svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
              )}
            </button>
          ) : null}
          <div className="rounded-full bg-violet-500/12 px-3 py-1">
            <span className="font-display text-sm font-bold tabular-nums text-violet-300">{formatDuration(elapsedSeconds)}</span>
          </div>
        </div>
      </div>

      {chatOverlayMessages.length ? (
        <div className={`yano-overlay-chat ${chatOpen ? (keyboardOpen ? "yano-overlay-chat--keyboard" : "yano-overlay-chat--raised") : ""}`}>
          {chatOverlayMessages.map((message) => {
            const self = message.sender?.id === currentUserId

            return (
              <div key={message.id} className="yano-overlay-chat__line">
                <span className={`yano-overlay-chat__name ${self ? "yano-overlay-chat__name--self" : "yano-overlay-chat__name--other"}`}>
                  {self ? t("call.you") : message.sender?.display_name || guestLabel}
                </span>
                <span className="text-lg font-semibold text-white/90 sm:text-base">{message.body}</span>
              </div>
            )
          })}
        </div>
      ) : null}

      {participantsOpen ? (
        <div className="yano-participants-panel">
          <div className="flex items-center justify-between border-b border-violet-500/10 px-4 py-3">
            <h3 className="font-display text-sm font-bold text-violet-200">{t("call.participants")}</h3>
            <div className="flex items-center gap-2">
              {isHost ? (
                <button
                  type="button"
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition ${roomLocked ? "bg-violet-400/20 text-violet-200" : "bg-violet-500/10 text-violet-400/70 hover:bg-violet-500/15 hover:text-violet-300"}`}
                  onClick={roomLocked ? onUnlockRoom : onLockRoom}
                  title={roomLocked ? t("call.unlock_room") : t("call.lock_room")}
                >
                  {roomLocked ? <LockClosedIcon className="h-3.5 w-3.5" /> : <LockOpenIcon className="h-3.5 w-3.5" />}
                  {roomLocked ? t("call.locked") : t("call.unlocked")}
                </button>
              ) : roomLocked ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/10 px-2.5 py-1 text-xs font-semibold text-violet-400/70">
                  <LockClosedIcon className="h-3.5 w-3.5" />
                  {t("call.locked")}
                </span>
              ) : null}
              <button type="button" className="rounded-full p-1 text-violet-400/60 transition hover:text-violet-200" onClick={() => setParticipantsOpen(false)}>
                <XCircleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
          <ul className="flex-1 overflow-y-auto px-2 py-2">
            {participantTiles.map(({ key, participant }) => {
              const isSelf = key === "local"
              const name = displayNameFor(participant, isSelf ? localDisplayName : guestLabel)
              const backendUserId = participant.user_id ? parseInt(participant.user_id.replace("user-", ""), 10) : null
              const backendMember = backendUserId ? roomMembers.find((m) => m.id === backendUserId) : null
              const isHostMember = backendMember?.role === "host"
              const mutedByHost = backendMember?.muted_by_host || false
              const hasAudio = hasPlayableTrack(participant, "audio")

              const dm = participantDmInfo(participant, isSelf)
              return (
                <li key={key} className="flex items-center gap-1 rounded-xl pr-2 transition hover:bg-violet-500/8">
                  <button
                    type="button"
                    onClick={() => handleParticipantTap(participant, isSelf)}
                    className="flex min-w-0 flex-1 items-center gap-2.5 rounded-xl px-2.5 py-2 text-left"
                    aria-label={dm?.active ? t("call.dm_active_indicator") : t("call.profile_open_actions")}
                  >
                    <AvatarBadge
                      name={name}
                      avatarUrl={backendMember?.avatar_url}
                      className={`grid h-8 w-8 shrink-0 place-items-center rounded-full bg-violet-500/15 text-xs font-bold text-violet-300 ${hasAudio ? "ring-2 ring-violet-400/30" : ""}`}
                      imgClassName="h-full w-full object-cover"
                    >
                      {dm?.active ? (
                        <span className={`yano-dm-dot ${dm.unread > 0 ? "" : "yano-dm-dot--read"}`} style={{ top: "-3px", right: "-3px" }}>
                          {dm.unread > 0 ? (dm.unread > 9 ? "9+" : dm.unread) : null}
                        </span>
                      ) : null}
                    </AvatarBadge>
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-1.5 truncate text-sm font-semibold text-violet-100">
                        <span className="truncate">{name}</span>
                        {isSelf ? <span className="text-violet-400/50">{t("call.you_suffix")}</span> : null}
                        {dm?.active ? <ChatBubbleLeftRightIcon className="h-3 w-3 shrink-0 text-violet-300/70" /> : null}
                      </p>
                      {isHostMember ? <p className="text-[10px] font-semibold text-violet-400/50">{t("room.host")}</p> : null}
                    </div>
                  </button>
                  {isHost && !isSelf && !isHostMember && backendUserId ? (
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        className="rounded-full p-1.5 text-violet-400/50 transition hover:bg-violet-500/15 hover:text-violet-200"
                        onClick={() => onMuteMember?.(backendUserId, !mutedByHost)}
                        title={mutedByHost ? t("call.unmute_member") : t("call.mute_member")}
                      >
                        {mutedByHost ? <SpeakerXMarkIcon className="h-4 w-4" /> : <SpeakerWaveIcon className="h-4 w-4" />}
                      </button>
                      <button
                        type="button"
                        className="rounded-full p-1.5 text-violet-400/50 transition hover:bg-red-500/15 hover:text-red-300"
                        onClick={() => onKickMember?.(backendUserId)}
                        title={t("call.kick_member")}
                      >
                        <XCircleIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : null}
                  {!isHost && mutedByHost ? (
                    <SpeakerXMarkIcon className="h-4 w-4 shrink-0 text-violet-400/40" title={t("call.muted_by_host")} />
                  ) : null}
                </li>
              )
            })}
          </ul>
          {isHost && pendingMembers.length > 0 ? (
            <div className="border-t border-violet-500/10 px-2 py-2">
              <p className="px-2.5 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-violet-400/50">{t("call.waiting_to_join")}</p>
              {pendingMembers.map((member) => (
                <div key={member.id} className="flex items-center gap-2.5 rounded-xl px-2.5 py-2 transition hover:bg-violet-500/8">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-amber-500/15 text-xs font-bold text-amber-300">
                    {(member.display_name || "?").slice(0, 2)}
                  </span>
                  <p className="min-w-0 flex-1 truncate text-sm font-semibold text-violet-100">{member.display_name}</p>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className="rounded-full p-1.5 text-emerald-400/70 transition hover:bg-emerald-500/15 hover:text-emerald-300"
                      onClick={() => onApproveMember?.(member.id)}
                      title={t("call.approve")}
                    >
                      <CheckCircleIcon className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      className="rounded-full p-1.5 text-violet-400/50 transition hover:bg-red-500/15 hover:text-red-300"
                      onClick={() => onDenyMember?.(member.id)}
                      title={t("call.deny")}
                    >
                      <XCircleIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {chatOpen ? (
        <div
          className={`yano-ui-layer pointer-events-auto absolute inset-x-0 z-10 px-4 ${uiHidden ? "yano-ui-layer--hidden" : ""}`}
          style={{ bottom: keyboardOpen ? "0.35rem" : "5.25rem" }}
        >
          <form
            className="mx-auto flex max-w-lg items-center gap-2"
            onSubmit={(event) => { event.preventDefault(); handleChatSend() }}
            onClick={(event) => event.stopPropagation()}
          >
            <input
              ref={chatInputRef}
              type="text"
              enterKeyHint="send"
              autoComplete="off"
              autoCorrect="off"
              onFocus={handleChatInputFocus}
              onBlur={handleChatInputBlur}
              className="flex-1 rounded-2xl border border-white/10 bg-black/40 px-4 py-2.5 text-sm font-semibold text-white outline-none backdrop-blur-sm transition placeholder:text-white/35 focus:border-violet-400/40"
              value={chatDraft}
              onChange={(event) => setChatDraft(event.target.value)}
              placeholder={t("chat.input_placeholder")}
            />
            <button
              type="button"
              disabled={chatSending || !chatDraft.trim()}
              onPointerDown={(event) => { event.preventDefault(); handleChatSend() }}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 text-white/80 backdrop-blur-sm transition hover:bg-white/18 disabled:opacity-30"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" /></svg>
            </button>
          </form>
        </div>
      ) : null}

      <div className="flex h-full flex-col px-3 pb-[5.5rem] pt-[4.5rem] sm:px-4 sm:pb-24 sm:pt-20">
        {error ? (
          <div className="mx-auto mb-3 w-full max-w-2xl rounded-[1.3rem] bg-violet-500/10 px-4 py-3 text-sm text-violet-200">
            {error}
          </div>
        ) : null}

	        {useFocusLayout ? (
	          <div className="relative min-h-0 flex-1 overflow-hidden rounded-[1.6rem]">
	            {!leadRemote ? (
	              <div className="flex h-full flex-col">
	                {localHasVideo && videoTrackFor(localParticipant) ? (
	                  <div className="min-h-0 flex-1 overflow-hidden rounded-[1.6rem]">
	                    <ParticipantVideoFx
	                      track={videoTrackFor(localParticipant)}
	                      mirrored
	                      muted
	                      effects={localFx}
	                      fit="contain"
	                      className="h-full w-full object-contain"
	                    />
	                  </div>
	                ) : null}
	                <div className="shrink-0 py-3 text-center sm:py-5">
	                  <p className="font-display text-xl leading-none text-violet-100 sm:text-2xl">{t(waitingStateLabelKey(status))}</p>
	                  {roomCode || shareUrl ? (
	                    <div className="mt-4 flex flex-col items-center gap-3">
	                      {roomCode ? (
	                        <>
	                          <div className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-violet-300/55">
	                            {t("call.invite_code")}
	                          </div>
	                          <div className="inline-flex items-center justify-center rounded-full bg-violet-500/12 px-3 py-1.5 text-[11px] font-extrabold tracking-[0.2em] text-violet-200/90">
	                            {roomCode}
	                          </div>
	                        </>
	                      ) : null}
	                      {shareUrl ? (
	                        <button type="button" onClick={onCopyLink} className="inline-flex items-center gap-2 rounded-2xl bg-violet-500/12 px-4 py-2.5 text-sm font-bold text-violet-300 transition hover:bg-violet-500/20">
	                          <CopyIcon />
	                          {copied ? t("home.copied") : t("home.copy_link")}
	                        </button>
	                      ) : null}
	                    </div>
	                  ) : null}
	                </div>
	              </div>
	            ) : bothHaveVideo ? (
	              <>
	                {mainParticipantVideoOn ? (
	                  <>
	                    <ParticipantVideoFx
	                      track={mainParticipantVideo}
	                      mirrored={mainParticipant === localParticipant}
	                      muted={mainParticipant === localParticipant}
	                      effects={mainParticipant === localParticipant ? localFx : mainParticipant?.userData?.yano_fx}
	                      fit="contain"
	                      className="h-full w-full object-contain"
	                    />
	                    {latestMessageFor(mainParticipant) ? (
	                      <div className="absolute inset-x-4 top-4 z-[5]">
	                        <TileMessage message={latestMessageFor(mainParticipant)} />
	                      </div>
	                    ) : null}
	                  </>
	                ) : (
	                  <ParticipantTile
	                    participant={mainParticipant}
	                    isLocal={mainParticipant === localParticipant}
                      avatarUrl={avatarUrlForParticipant(mainParticipant, mainParticipant === localParticipant)}
	                    fallbackLabel={mainParticipant === localParticipant ? localDisplayName : guestLabel}
	                    effectsOverride={mainParticipant === localParticipant ? localFx : null}
                    hideOverlay={uiHidden}
                    latestMessage={latestMessageFor(mainParticipant)}
                    onProfileTap={handleParticipantTap}
                    dmInfo={participantDmInfo(mainParticipant, mainParticipant === localParticipant)}
	                  />
	                )}
	              </>
            ) : neitherHasVideo ? (
              <div className="flex h-full items-center justify-center gap-16">
                <AudioIndicator
                  name={displayNameFor(leadRemote, guestLabel)}
                  avatarUrl={avatarUrlForParticipant(leadRemote, false)}
                  isLocal={false}
                  speaking={remoteHasAudio}
                  latestMessage={latestMessageFor(leadRemote)}
                  onTap={leadRemote ? () => handleParticipantTap(leadRemote, false) : null}
                  dmInfo={participantDmInfo(leadRemote, false)}
                />
                <AudioIndicator
                  name={localDisplayName}
                  avatarUrl={avatarUrlForParticipant(localParticipant, true)}
                  isLocal
                  speaking={resolvedLocalAudio}
                  latestMessage={latestMessageFor(localParticipant)}
                  onTap={localParticipant ? () => handleParticipantTap(localParticipant, true) : null}
                  dmInfo={participantDmInfo(localParticipant, true)}
                />
              </div>
	            ) : (
	              <>
		                {remoteHasVideo ? (
		                  <>
		                    <ParticipantVideoFx
		                      track={videoTrackFor(leadRemote)}
		                      mirrored={false}
		                      muted={false}
		                      effects={leadRemote?.userData?.yano_fx}
		                      fit="contain"
		                      className="h-full w-full object-contain"
		                    />
		                    {latestMessageFor(leadRemote) ? (
		                      <div className="absolute inset-x-4 top-4 z-[5]">
		                        <TileMessage message={latestMessageFor(leadRemote)} />
		                      </div>
		                    ) : null}
		                  </>
		                ) : localHasVideo && videoTrackFor(localParticipant) ? (
		                  <>
		                    <ParticipantVideoFx
		                      track={videoTrackFor(localParticipant)}
		                      mirrored
		                      muted
		                      effects={localFx}
		                      fit="contain"
		                      className="h-full w-full object-contain"
		                    />
		                    {latestMessageFor(localParticipant) ? (
		                      <div className="absolute inset-x-4 top-4 z-[5]">
		                        <TileMessage message={latestMessageFor(localParticipant)} />
		                      </div>
		                    ) : null}
		                  </>
		                ) : (
	                  <div className="flex h-full items-center justify-center">
                    <ParticipantTile
                      participant={remoteHasVideo ? localParticipant : leadRemote}
                      isLocal={!remoteHasVideo}
                      avatarUrl={avatarUrlForParticipant(remoteHasVideo ? localParticipant : leadRemote, !remoteHasVideo)}
                      fallbackLabel={remoteHasVideo ? localDisplayName : guestLabel}
                      effectsOverride={!remoteHasVideo ? null : localFx}
                      hideOverlay={uiHidden}
                      latestMessage={latestMessageFor(remoteHasVideo ? localParticipant : leadRemote)}
                      onProfileTap={handleParticipantTap}
                      dmInfo={participantDmInfo(remoteHasVideo ? localParticipant : leadRemote, !remoteHasVideo)}
	                    />
	                  </div>
	                )}
                <div data-pip className="absolute bottom-4 right-4 z-10 flex items-center gap-2.5 rounded-2xl border border-violet-400/15 bg-[#1a0e2e]/80 px-3 py-2.5 shadow-2xl backdrop-blur-sm md:right-6">
                  <AvatarBadge
                    name={remoteHasVideo ? localDisplayName : displayNameFor(leadRemote, guestLabel)}
                    avatarUrl={avatarUrlForParticipant(remoteHasVideo ? localParticipant : leadRemote, remoteHasVideo)}
                    className={`grid h-9 w-9 place-items-center rounded-full bg-violet-500/15 text-sm font-bold text-violet-200 ${remoteHasVideo ? (resolvedLocalAudio ? "ring-2 ring-violet-400/30" : "") : (remoteHasAudio ? "ring-2 ring-violet-400/30" : "")}`}
                    imgClassName="h-full w-full object-cover"
                  />
                  <div>
                    <p className="text-xs font-bold text-violet-200">{remoteHasVideo ? t("call.you") : displayNameFor(leadRemote, guestLabel)}</p>
                    <p className="text-[10px] text-violet-400/50">{(remoteHasVideo ? resolvedLocalAudio : remoteHasAudio) ? t("call.audio_on") : t("call.muted")}</p>
                  </div>
                </div>
              </>
            )}

	            {leadRemote && bothHaveVideo && pipParticipant ? (
	              <button
                data-pip
                type="button"
                className="absolute bottom-4 right-4 z-10 h-24 w-32 overflow-hidden rounded-[1.15rem] border-2 border-violet-400/15 shadow-2xl transition-all duration-300 md:right-6 md:h-32 md:w-44"
                onClick={() => setSelfIsMain((value) => !value)}
                aria-label={t("call.swap_main_video")}
	              >
	                <ParticipantVideoFx
	                  track={pipVideo}
	                  mirrored={pipParticipant === localParticipant}
	                  muted={pipParticipant === localParticipant}
	                  effects={pipParticipant === localParticipant ? localFx : pipParticipant?.userData?.yano_fx}
	                  fit="cover"
	                  className="h-full w-full object-cover"
	                />
	                <div className="absolute bottom-2 left-2 rounded-2xl bg-[#1a0e2e]/80 px-2 py-0.5 text-[10px] font-bold text-violet-200 backdrop-blur-sm">
	                  {pipParticipant === localParticipant ? t("call.you") : pipName}
	                </div>
              </button>
            ) : null}
          </div>
        ) : videoParticipants.length === 0 ? (
          <div className="flex min-h-0 flex-1 items-center justify-center gap-10 sm:gap-16">
            {participantTiles.map(({ key, participant }) => (
              <AudioIndicator
                key={key}
                name={displayNameFor(participant, key === "local" ? localDisplayName : guestLabel)}
                avatarUrl={avatarUrlForParticipant(participant, key === "local")}
                speaking={hasPlayableTrack(participant, "audio")}
                latestMessage={latestMessageFor(participant)}
                onTap={() => handleParticipantTap(participant, key === "local")}
                dmInfo={participantDmInfo(participant, key === "local")}
              />
            ))}
          </div>
        ) : videoParticipants.length < participantTiles.length ? (
          <div className="flex min-h-0 flex-1 flex-col gap-3 md:flex-row">
	            <div className={`min-h-0 flex-1 ${videoParticipants.length === 1 ? "" : "grid grid-cols-1 gap-3 sm:grid-cols-2"}`}>
	              {videoParticipants.map(({ key, participant }) => (
	                <ParticipantTile
	                  key={key}
	                  participant={participant}
	                  isLocal={key === "local"}
                    avatarUrl={avatarUrlForParticipant(participant, key === "local")}
	                  fallbackLabel={key === "local" ? localDisplayName : guestLabel}
	                  effectsOverride={key === "local" ? localFx : null}
                  hideOverlay={uiHidden}
                  latestMessage={latestMessageFor(participant)}
                  onProfileTap={handleParticipantTap}
                  dmInfo={participantDmInfo(participant, key === "local")}
	                />
	              ))}
	            </div>
            <div className="flex shrink-0 gap-3 md:w-48 md:flex-col md:justify-center">
              {nonVideoParticipants.map(({ key, participant }) => {
                const dm = participantDmInfo(participant, key === "local")
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleParticipantTap(participant, key === "local")}
                    className="relative flex flex-1 items-center gap-2.5 rounded-2xl border border-violet-400/15 bg-violet-500/8 p-3 text-left transition hover:bg-violet-500/12 active:scale-[0.98] md:flex-col md:py-5 md:text-center"
                    aria-label={dm?.active ? t("call.dm_active_indicator") : t("call.profile_open_actions")}
                  >
                    <AvatarBadge
                      name={displayNameFor(participant, key === "local" ? localDisplayName : guestLabel)}
                      avatarUrl={avatarUrlForParticipant(participant, key === "local")}
                      className={`grid h-10 w-10 shrink-0 place-items-center rounded-full bg-violet-500/15 text-sm font-bold text-violet-200 ${hasPlayableTrack(participant, "audio") ? "ring-2 ring-violet-400/30" : ""}`}
                      imgClassName="h-full w-full object-cover"
                    >
                      {dm?.active ? (
                        <span className={`yano-dm-dot ${dm.unread > 0 ? "" : "yano-dm-dot--read"}`} style={{ top: "-3px", right: "-3px" }}>
                          {dm.unread > 0 ? (dm.unread > 9 ? "9+" : dm.unread) : null}
                        </span>
                      ) : null}
                    </AvatarBadge>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-violet-200">{displayNameFor(participant, key === "local" ? localDisplayName : guestLabel)}{key === "local" ? ` ${t("call.you_suffix")}` : ""}</p>
                      <p className="text-[10px] text-violet-400/50">{hasPlayableTrack(participant, "audio") ? t("call.audio_on") : t("call.muted")}</p>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        ) : (
	            <div className={`grid min-h-0 flex-1 gap-3 ${gridClassName}`}>
	              {participantTiles.map(({ key, participant }) => (
	                <ParticipantTile
	                  key={key}
	                  participant={participant}
	                  isLocal={key === "local"}
                    avatarUrl={avatarUrlForParticipant(participant, key === "local")}
	                  fallbackLabel={key === "local" ? localDisplayName : guestLabel}
	                  effectsOverride={key === "local" ? localFx : null}
                  hideOverlay={uiHidden}
                  latestMessage={latestMessageFor(participant)}
                  onProfileTap={handleParticipantTap}
                  dmInfo={participantDmInfo(participant, key === "local")}
	                />
	              ))}
	            </div>
        )}
      </div>

      {remoteAudioTracks.map(({ key, track }) => (
        <ParticipantAudio key={key} track={track} />
      ))}

      <div
        data-pip
        className={`yano-ui-layer pointer-events-auto absolute inset-x-0 bottom-[5.25rem] z-10 px-4 ${uiHidden ? "yano-ui-layer--hidden" : ""}`}
      >
        <div className="mx-auto w-full max-w-lg">
          <div
            className="grid transition-[grid-template-rows] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]"
            style={{ gridTemplateRows: effectsOpen ? "1fr" : "0fr" }}
          >
            <div className="overflow-hidden">
              <div className={`rounded-[1.35rem] border border-violet-500/12 bg-[#120826]/70 p-3 shadow-2xl backdrop-blur-md ${effectsOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1"} transition-[opacity,transform] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]`}>
                <div className="flex flex-wrap gap-2">
                  {VIDEO_GRADES.map((effect) => {
                    const selected = localFx.grade === effect.id
                    return (
                      <button
                        key={effect.id}
                        type="button"
                        onClick={() => updateLocalFx({ grade: effect.id })}
                        className={`rounded-2xl border px-3 py-2 text-xs font-extrabold tracking-[0.12em] transition ${
                          selected
                            ? "border-violet-400/30 bg-violet-500/18 text-violet-100"
                            : "border-violet-500/12 bg-black/10 text-violet-300/70 hover:bg-violet-500/10 hover:text-violet-200"
                        }`}
                        title={t(`effects.grade.${effect.id}`)}
                        aria-pressed={selected}
                      >
                        {t(`effects.grade.${effect.id}`)}
                      </button>
                    )
                  })}
                </div>

                <div className="mt-3">
                  <div className="h-px w-full bg-violet-500/10" />
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => updateLocalFx({ soft: !localFx.soft })}
                      className={`rounded-2xl border px-3 py-2 text-xs font-extrabold tracking-[0.12em] transition ${
                        localFx.soft
                          ? "border-violet-400/30 bg-violet-500/18 text-violet-100"
                          : "border-violet-500/12 bg-black/10 text-violet-300/70 hover:bg-violet-500/10 hover:text-violet-200"
                      }`}
                      aria-pressed={localFx.soft}
                      title={t("effects.soft")}
                    >
                      {t("effects.soft")}
                    </button>
                    <button
                      type="button"
                      onClick={() => updateLocalFx({ vignette: !localFx.vignette })}
                      className={`rounded-2xl border px-3 py-2 text-xs font-extrabold tracking-[0.12em] transition ${
                        localFx.vignette
                          ? "border-violet-400/30 bg-violet-500/18 text-violet-100"
                          : "border-violet-500/12 bg-black/10 text-violet-300/70 hover:bg-violet-500/10 hover:text-violet-200"
                      }`}
                      aria-pressed={localFx.vignette}
                      title={t("effects.vignette")}
                    >
                      {t("effects.vignette")}
                    </button>
                    <button
                      type="button"
                      onClick={() => updateLocalFx({ faceBlur: !localFx.faceBlur })}
                      className={`rounded-2xl border px-3 py-2 text-xs font-extrabold tracking-[0.12em] transition ${
                        localFx.faceBlur
                          ? "border-violet-400/30 bg-violet-500/18 text-violet-100"
                          : "border-violet-500/12 bg-black/10 text-violet-300/70 hover:bg-violet-500/10 hover:text-violet-200"
                      }`}
                      aria-pressed={localFx.faceBlur}
                      title={t("effects.face_viewer_side")}
                    >
                      {t("effects.face")}
                    </button>
                  </div>
                </div>

                {localFx.faceBlur ? (
                  <div className="mt-3 rounded-2xl border border-violet-500/12 bg-black/10 px-3 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-[11px] font-extrabold tracking-[0.14em] text-violet-200/90">{t("effects.face")}</div>
                      <div className="text-[11px] font-bold text-violet-300/60">{localFx.faceBlurPx}px</div>
                    </div>
                    <input
                      className="yano-range mt-2 w-full"
                      type="range"
                      min="2"
                      max="9"
                      step="1"
                      value={localFx.faceBlurPx}
                      onChange={(event) => updateLocalFx({ faceBlurPx: Number(event.target.value) })}
                      aria-label={t("effects.face_strength")}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`yano-ui-layer absolute inset-x-0 bottom-4 z-10 flex justify-center px-4 ${uiHidden || keyboardOpen ? "yano-ui-layer--hidden" : ""}`}>
        <div className="inline-flex items-center gap-3 text-white">
          <ControlButton active={resolvedLocalAudio} label={resolvedLocalAudio ? t("mic.on") : t("mic.off")} onClick={toggleLocalAudio}>
            <MicIcon />
          </ControlButton>
          <ControlButton active={resolvedLocalVideo} label={resolvedLocalVideo ? t("video.on") : t("video.off")} onClick={toggleLocalVideo}>
            <VideoIcon />
          </ControlButton>
          <ControlButton
            label={t("effects.title")}
            onClick={() => setEffectsOpen((open) => !open)}
            pressed={effectsOpen}
            className={effectsOpen ? "ring-2 ring-violet-400/35" : ""}
          >
            <EffectsIcon />
          </ControlButton>
          {onToggleChat ? (
            <ControlButton label={chatOpen ? t("call.close_chat") : t("call.open_chat")} onClick={onToggleChat} className={chatOpen ? "ring-2 ring-violet-400/35" : ""}>
              <span className="relative inline-flex">
                <ChatIcon />
                {unreadCount > 0 ? <span className="yano-chat-badge">{unreadCount > 9 ? "9+" : unreadCount}</span> : null}
              </span>
            </ControlButton>
          ) : null}
          <ControlButton
            label={participantsOpen ? t("call.close_participants") : t("call.open_participants")}
            onClick={() => setParticipantsOpen((open) => !open)}
            className={showPendingJoinNotice ? "yano-call-control--attention" : ""}
          >
            <span className="relative inline-flex">
              <UsersIcon className="h-[1.125rem] w-[1.125rem]" />
              {showPendingJoinNotice ? (
                <span className="yano-pending-badge" aria-hidden="true">
                  {pendingJoinCount > 9 ? "9+" : pendingJoinCount}
                </span>
              ) : null}
              <span className="absolute -right-1.5 -top-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-violet-400 px-1 text-[10px] font-bold leading-none text-[#1a0e2e]">{participantTiles.length}</span>
            </span>
          </ControlButton>
          <ControlButton danger label={leaveArmed ? t("call.confirm_leave") : t("call.leave_room")} onClick={handleLeaveClick} wide={leaveArmed}>
            <PhoneXMarkIcon className="h-[1.125rem] w-[1.125rem]" />
          </ControlButton>
        </div>
      </div>

      <ParticipantActionSheet target={actionSheetTarget} onClose={closeActionSheet} onDm={openDm} />
      <OneToOneDmPanel
        target={currentDmTarget}
        messages={currentDmMessages}
        loading={Boolean(currentDmConv?.loading)}
        sending={Boolean(currentDmConv?.sending)}
        error={currentDmConv?.error || ""}
        onMinimize={minimizeDm}
        onSend={sendDmMessage}
        onAvatarTap={handleDmAvatarTap}
        formatTime={formatDmTime}
      />
      <DmPreviewToast toast={dmState.toast} onTap={handleToastTap} onDismiss={dismissDmToast} />
    </div>
  )
}
